import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import firebase from 'firebase/app';
import 'firebase/firestore';

export interface CmoveQuotaDoc {
  quota: number;
  quotaOverride?: number | null;
  currentMonth: string; // "YYYY-MM"
  usageCount: number;
  lastUpdated: firebase.firestore.Timestamp;
}

export class CmoveQuotaExceededError extends Error {
  constructor(public used: number, public quota: number) {
    super(`C-MOVE quota exceeded: ${used}/${quota} this month`);
    this.name = 'CmoveQuotaExceededError';
  }
}

const FALLBACK_QUOTA = 5;

@Injectable({ providedIn: 'root' })
export class CmoveQuotaService {
  private collection() {
    return firebase.firestore().collection('cmove_quotas');
  }

  /** Real-time stream of the quota document — drives the progress bar. */
  watchQuota(ownerID: string): Observable<CmoveQuotaDoc | null> {
    return new Observable(observer => {
      const unsub = this.collection()
        .doc(ownerID)
        .onSnapshot(
          snap => observer.next(snap.exists ? (snap.data() as CmoveQuotaDoc) : null),
          err => observer.error(err)
        );
      return unsub;
    });
  }

  /**
   * Atomically checks the quota and increments usageCount by `count`.
   * - On first use: reads plans/{plancode}.cmoveMonthlyQuota to seed the quota doc.
   * - Resets usageCount when the calendar month rolls over.
   * - Throws CmoveQuotaExceededError if the send would exceed the effective limit.
   * - Effective limit = quotaOverride ?? quota (from plan) ?? FALLBACK_QUOTA
   */
  async checkAndConsume(ownerID: string, plancode: string, count: number): Promise<void> {
    const quotaRef = this.collection().doc(ownerID);
    const thisMonth = this.currentMonth();

    await firebase.firestore().runTransaction(async tx => {
      const snap = await tx.get(quotaRef);

      if (!snap.exists) {
        // First use — seed the quota from the plan doc
        const planQuota = await this.resolvePlanQuota(plancode);
        const effectiveQuota = planQuota ?? FALLBACK_QUOTA;

        if (count > effectiveQuota) {
          throw new CmoveQuotaExceededError(0, effectiveQuota);
        }

        tx.set(quotaRef, {
          quota: effectiveQuota,
          quotaOverride: null,
          currentMonth: thisMonth,
          usageCount: count,
          lastUpdated: firebase.firestore.FieldValue.serverTimestamp(),
        });
        return;
      }

      const data = snap.data() as CmoveQuotaDoc;
      const isNewMonth = data.currentMonth !== thisMonth;
      const currentUsage = isNewMonth ? 0 : data.usageCount;
      const effectiveQuota = data.quotaOverride ?? data.quota ?? FALLBACK_QUOTA;

      if (currentUsage + count > effectiveQuota) {
        throw new CmoveQuotaExceededError(currentUsage, effectiveQuota);
      }

      if (isNewMonth) {
        tx.update(quotaRef, {
          currentMonth: thisMonth,
          usageCount: count,
          lastUpdated: firebase.firestore.FieldValue.serverTimestamp(),
        });
      } else {
        tx.update(quotaRef, {
          usageCount: firebase.firestore.FieldValue.increment(count),
          lastUpdated: firebase.firestore.FieldValue.serverTimestamp(),
        });
      }
    });
  }

  private async resolvePlanQuota(plancode: string): Promise<number | null> {
    if (!plancode) return null;
    try {
      const snap = await firebase.firestore().collection('plans').doc(plancode).get();
      if (snap.exists) {
        const data = snap.data() as { cmoveMonthlyQuota?: number };
        return data.cmoveMonthlyQuota ?? null;
      }
    } catch (err) {
      console.warn('[CmoveQuota] Could not read plan quota:', err);
    }
    return null;
  }

  private currentMonth(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }
}
